import { useEventListener } from '@vueuse/core';

export default function useTouchEvents({
  svg, emit, svgViewBox, eventmap, containerWidth, containerHeight, isPanning, isPinching, isResizing, isPolyPointMoving, isDragging, isPathNodeMoving,
}) {
  const { pan, viewBox } = svgViewBox;

  // Variables for pinch-to-zoom and panning
  let initialDistance = null; // Initial distance between two fingers for pinch
  let initialViewBoxState = null; // Initial state of the viewBox
  const minZoom = 0.05; // Minimum zoom level
  const maxZoom = 2.0; // Maximum zoom level
  let touchTimeout; // Timeout for detecting long press
  let isLongPress = false; // Flag for long press detection
  let initialTouchX = 0; // Initial X position for touch
  let initialTouchY = 0; // Initial Y position for touch
  const zoomSensitivity = 1; // Sensitivity for zooming
  let animationFrameId; // ID for animation frame to optimize rendering

  const applyViewBox = (newViewBox) => {
    viewBox.value = newViewBox.map((v, i) => (i >= 2 && v < 1 ? 1 : v)); // Ensure minimum dimensions
  };

  const handleMove = (deltaX, deltaY) => {
    if (isResizing.value || isPolyPointMoving.value !== false) return;

    if (!isDragging.value && !isPathNodeMoving.value) {
      isPanning.value = true;
      pan(-deltaX, -deltaY);
    }
  };

  const pinchToZoom = (event) => {
    const [touch1, touch2] = event.touches; // Get two touch points
    const dx = touch2.clientX - touch1.clientX; // Distance in X
    const dy = touch2.clientY - touch1.clientY; // Distance in Y
    const distance = Math.sqrt(dx * dx + dy * dy); // Calculate distance between fingers

    if (!initialDistance) {
      // Initialize pinch gesture
      initialDistance = distance;
      initialViewBoxState = [...viewBox.value];
      return;
    }

    // Calculate zoom scale
    const scale = (distance / initialDistance) ** zoomSensitivity;
    const newWidth = Math.max(
      Math.min(initialViewBoxState[2] / scale, eventmap.width * maxZoom),
      eventmap.width * minZoom,
    );
    const newHeight = newWidth * (containerHeight.value / containerWidth.value);
    const centreX = initialViewBoxState[0] + initialViewBoxState[2] / 2;
    const centreY = initialViewBoxState[1] + initialViewBoxState[3] / 2;

    // Calculate new viewBox
    const newViewBox = [
      centreX - newWidth / 2,
      centreY - newHeight / 2,
      newWidth,
      newHeight,
    ];

    // Apply new viewBox with animation frame
    cancelAnimationFrame(animationFrameId);
    animationFrameId = window.requestAnimationFrame(() => applyViewBox(newViewBox));
  };

  const initializeTouchListeners = () => {
    // Handle touch start
    useEventListener(svg, 'touchstart', (event) => {
      if (event.touches.length === 1 && !isPinching.value) {
        // Single touch: detect long press
        clearTimeout(touchTimeout);
        isLongPress = false;
        touchTimeout = setTimeout(() => { isLongPress = true; }, 500);
        initialTouchX = event.touches[0].clientX;
        initialTouchY = event.touches[0].clientY;
      } else if (event.touches.length === 2) {
        // Two fingers: start pinch gesture
        event.preventDefault();
        isPinching.value = true;
      }
    }, { passive: false });

    // Handle touch move
    useEventListener(svg, 'touchmove', (event) => {
      if (event.touches.length === 1) {
        // Single touch: handle panning
        const touch = event.touches[0];
        const deltaX = initialTouchX - touch.clientX;
        const deltaY = initialTouchY - touch.clientY;
        initialTouchX = touch.clientX;
        initialTouchY = touch.clientY;
        handleMove(-deltaX, -deltaY); // Pass inverted deltas
      } else if (event.touches.length === 2) {
        // Two fingers: handle pinch-to-zoom
        pinchToZoom(event);
      }
    }, { passive: false });

    // Handle touch end
    useEventListener(svg, 'touchend', () => {
      clearTimeout(touchTimeout);
      if (!isLongPress) emit('svgClicked'); // Emit click event if not long press
      isPanning.value = false; // Reset panning state
      initialDistance = null; // Reset pinch state
      setTimeout(() => {
        isPinching.value = false; // Allow new pinch gesture
      }, 100);
    });
  };

  // Return the initializer function
  return {
    initializeTouchListeners,
  };
}
